'use client'

import Link from 'next/link'
import { LuChevronDown, LuLightbulb } from 'react-icons/lu'

import DragRail from '@/core/components/optional/drag-rail'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'

import DetailBox from '@/features/veluvit/components/detail/detail-box'
import { GemAmount } from '@/features/veluvit/components/ui/currency-icon'
import ProductMeta from '@/features/veluvit/components/ui/product-meta'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { productCopy } from '@/features/veluvit/lib/format'
import { to } from '@/features/veluvit/lib/route'
import { cmdsFor, workFor } from '@/features/veluvit/lib/rules'
import { useRoom } from '@/features/veluvit/stores/session.store'

/* 상세 화면 하단 상자들 — 등장인물 · 명령어 · 플레이 가이드 · 작가의 다른 작품. 각각 짧고 상세 화면에서만 쓰여 한 파일에 둔다.
   2026-10-08 스튜디오 기획으로 정리 — 스튜디오에 입력 칸이 없는 옛 레퍼런스 상자(작가 코멘트 · 업데이트 · 같은 작품 캐릭터 · 함께 만나볼 장면 · 고정 문구 '즐기는 방법')는 걷었다. */

const ACC_SUMMARY =
  'flex cursor-pointer list-none items-center gap-2 py-3 text-c14 transition-colors hover:text-t1 [&::-webkit-details-marker]:hidden'

/** 명령어 — 이 작품에서 쓸 수 있는 것(작가 명령어 + 켜 둔 공식 명령어 — 서버가 work.commands 로 합쳐 준다) */
export function CommandBox({ id }: { id: string }) {
  const { catalog } = useCatalogMedia()
  const room = useRoom(id)
  const list = cmdsFor(catalog, room, id)
  if (!list.length) return null
  return (
    <DetailBox title="명령어" sub="대화 중 ! 입력">
      {list.map((k) => (
        <details
          key={k.k}
          className="group border-t border-foreground/[0.06] first-of-type:border-t-0"
        >
          <summary className={ACC_SUMMARY}>
            <code className="rounded-control bg-point/15 px-2 py-0.5 [font-family:inherit] font-bold text-point">
              {k.name}
            </code>
            {k.desc.split(' ').slice(0, 3).join(' ')}…
            <span className="ml-auto text-c12 text-gem">
              {k.cost ? <GemAmount value={k.cost} /> : '무료'}
            </span>
            <LuChevronDown
              aria-hidden="true"
              className="size-4 flex-none text-t3 transition-transform duration-200 ease-smooth group-open:rotate-180"
            />
          </summary>
          <div className="pb-3 text-c13 text-t2">
            {k.desc}
            {k.tip && (
              <>
                <br />
                <span className="inline-flex items-start gap-1 text-t3">
                  <LuLightbulb
                    aria-hidden="true"
                    className="mt-0.5 size-3.5 flex-none"
                  />
                  {k.tip}
                </span>
              </>
            )}
          </div>
        </details>
      ))}
    </DetailBox>
  )
}

/**
 * 등장인물 — 여러 명이 나오는 작품(출연 캐릭터 2명 이상)만. 스튜디오 기획 §6.1 "여러명챗: 상세에 등장인물 줄".
 * 캐릭터 그림(2:3) · 이름 · 한 줄 소개. 서버 작품의 cast 를 먼저, 없으면 정적 시드의 cast.
 */
export function CastBox({ id }: { id: string }) {
  const { catalog, thumb } = useCatalogMedia()
  const work = workFor(catalog, id)
  const source: { name: string; role?: string; image?: string }[] = work?.cast
    ?.length
    ? work.cast
    : catalog.chars[id].cast || []
  const cast = source.filter((p) => p && p.name)
  if (cast.length < 2) return null
  return (
    <DetailBox title="등장인물" sub={`${cast.length}명`}>
      <DragRail
        ariaLabel="등장인물"
        className="-mx-4 mt-3 over10:mx-0"
        classNames={{ track: 'gap-2.5 px-4 over10:px-0' }}
      >
        {cast.map((person) => (
          <article key={person.name} className="w-28 flex-none snap-start">
            <div className="relative aspect-[2/3] overflow-hidden rounded-field bg-surface shadow-[inset_0_0_0_1px_rgb(255_255_255/0.06)]">
              {person.image ? (
                <SafeImage
                  src={person.image}
                  fallbackSrc={thumb(id)}
                  alt=""
                  fill
                  sizes="112px"
                  className="object-cover object-[50%_25%]"
                  appearance="plain"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="grid h-full place-items-center text-c28 font-bold text-t3"
                >
                  {person.name.charAt(0)}
                </span>
              )}
            </div>
            <h3 className="mt-1.75 truncate text-c14 font-bold text-t1">
              {person.name}
            </h3>
            {person.role && (
              <p className="mt-0.5 line-clamp-2 text-c12 leading-[1.5] text-t3 [overflow-wrap:anywhere]">
                {person.role}
              </p>
            )}
          </article>
        ))}
      </DragRail>
    </DetailBox>
  )
}

/**
 * 이 작가의 다른 작품 — 한 작품을 재밌게 한 이용자가 같은 작가의 다음 작품으로 넘어가게(기획: 작가 바이럴).
 * 서버의 sameCreator 또는 같은 작가 ID에 속한 공개 작품만 묶는다.
 */
export function CreatorWorks({ id }: { id: string }) {
  const { catalog, thumb } = useCatalogMedia()
  const c = catalog.chars[id]
  const work = workFor(catalog, id)
  const creator = work?.creator
  const author = productCopy(c.author)
  const sameCreator = (other: string) =>
    work?.sameCreator
      ? work.sameCreator.includes(other)
      : !!creator?.id && workFor(catalog, other)?.creator?.id === creator.id
  const others = catalog.order
    .filter(
      (other) => other !== id && !!catalog.chars[other] && sameCreator(other)
    )
    .slice(0, 12)
  if (!others.length) return null
  const name = creator?.name || author
  return (
    <DetailBox title={`${name} 작가의 다른 작품`} sub={`${others.length}개`}>
      <DragRail
        ariaLabel={`${name} 작가의 다른 작품`}
        className="-mx-4 mt-3 over10:mx-0"
        classNames={{ track: 'gap-2.5 px-4 over10:px-0' }}
      >
        {others.map((other) => {
          const o = catalog.chars[other]
          return (
            <Link
              key={other}
              href={to(`/c/${other}`)}
              className="w-30 flex-none snap-start text-t1 no-underline"
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded-field bg-surface shadow-[inset_0_0_0_1px_rgb(255_255_255/0.06)]">
                <SafeImage
                  src={thumb(other)}
                  alt=""
                  fill
                  sizes="120px"
                  className="object-cover object-[50%_30%]"
                  appearance="plain"
                />
                <WorkTitleOverlay
                  src={catalog.images[other]?.logo}
                  sizes="120px"
                />
              </div>
              <ProductMeta c={o} size="sm" />
            </Link>
          )
        })}
      </DragRail>
    </DetailBox>
  )
}

/** 플레이 가이드 — 작가가 이용자에게만 보여 주는 안내(스튜디오 Chat.playGuide). 쓰지 않았으면 상자째 숨긴다(예전 고정 문구는 걷었다) */
export function PlayGuide({ id }: { id: string }) {
  const { catalog } = useCatalogMedia()
  const guide = workFor(catalog, id)?.playGuide?.trim()
  if (!guide) return null
  return (
    <DetailBox title="플레이 가이드">
      <p className="mt-3 text-c14 leading-[1.8] whitespace-pre-line text-t2 [overflow-wrap:anywhere] over10:text-c15">
        {guide}
      </p>
    </DetailBox>
  )
}
